import React from 'react';
import { fmtMoney } from '../engine.js';
import {
  CURRENT_PERIOD_HEADER_CLS, DEFAULT_PERIOD_HEADER_CLS,
  CURRENT_PERIOD_CELL_TINT, THEAD_BOTTOM_LINE, THEAD_CORNER_SHADOW,
  STICKY_H_CELL, STICKY_H_CELL_LEFT, CURRENT_PERIOD_STICKY_BG,
  THEAD_VERT_PAD, STICKY_TREND_HEADER_CELL,
  STICKY_TREND_BODY_CELL, STICKY_TREND_SECTION_CELL, STICKY_TREND_BOX_GRID,
  STICKY_BOX_GRID,
} from './periodStyles.js';
import { RowSparkSegment, SparkDefs } from './RowSparkSegment.jsx';
import MiniSparkline, { SparklineDeltaBadge } from './MiniSparkline.jsx';
import CurrentPeriodProofBadge from './CurrentPeriodProofBadge.jsx';
import StatementFrame from './StatementFrame.jsx';
import StatementHeader from './StatementHeader.jsx';
import ProvenanceChip from './ProvenanceChip.jsx';
import { labelCellCls, amtCellCls } from './cellStyles.js';
import { fmtAmount as sharedFmtAmount, isFlatSeries, computeTrendTone, seriesSlope, resolveTrendVisual } from './formatAmount.js';
import { useStickyCascade } from '../hooks/useStickyCascade.js';
import Tooltip, { RichExplanation } from './Tooltip.jsx';
import { lookupLineGlossary } from './statementGlossary.js';
import {
  SOURCE_STATE_CLASSES,
  decorateStatementSectionsWithAggregateRowProof,
  formatMoneyProvenanceTooltip,
  sourceClassForMoneyProvenance,
} from '../source-state.js';

const FORMAL_LABEL_COL_WIDTH = 180;
const FORMAL_TREND_COL_WIDTH = 100;

/* Helper: wrap a label in a RichExplanation tooltip when the glossary
   has an entry, else render plain. `betterWhenFallback` is used when the
   glossary entry doesn't declare one (so the direction guide still lines
   up with how the row is colored). `enabled=false` short-circuits to
   plain-text so the Guide Controls panel can turn tooltips off globally. */
function LabelWithTooltip({ label, betterWhenFallback, className, enabled = true }) {
  const g = enabled ? lookupLineGlossary(label) : null;
  if (!g) return <span className={className}>{label}</span>;
  return (
    <Tooltip
      placement="right"
      delay={120}
      maxWidth={360}
      content={
        <RichExplanation
          title={label}
          formula={g.formula}
          hint={g.hint}
          explanation={g.explanation}
          direction={g.direction}
          betterWhen={g.betterWhen ?? betterWhenFallback}
        />
      }
    >
      <span className={`${className || ''} cursor-help`}>{label}</span>
    </Tooltip>
  );
}

function formatAggregateRowTooltip(line, aggregateRow) {
  const reportLabel = line?.__ledgerAggregateReportLabel || line?.__ledgerAggregateReportType || 'formal statement';
  const rowAuthorship = aggregateRow?.proofPosture?.rowAuthorship
    || aggregateRow?.sourceLineage?.sourceClass
    || 'ledger-service-aggregate-row';
  const amount = aggregateRow?.amounts?.presentationAmount?.amount
    ?? aggregateRow?.amounts?.signedAmount?.amount
    ?? aggregateRow?.amounts?.adjusted?.signedAmount?.amount
    ?? null;
  const unconverted = line?.__ledgerUnconvertedReportingCurrency || null;
  const parts = [
    `Ledger aggregate row proof: ${reportLabel}.`,
    `Row authorship: ${rowAuthorship}.`,
    amount != null ? `Contract amount: ${amount}${line?.__ledgerAmountCurrency ? ` ${line.__ledgerAmountCurrency}` : ''}.` : null,
    unconverted
      ? `Unconverted row: ${unconverted.amountCurrency || 'source currency'} preserved because presentationAmount is missing for reporting currency ${unconverted.reportingCurrency || 'unknown'}.`
      : null,
  ];
  if (Number.isFinite(aggregateRow?.sourceLineage?.journalLineCount)) {
    parts.push(`Journal-line atoms summarized: ${aggregateRow.sourceLineage.journalLineCount}.`);
  }
  if (line?.__ledgerAggregateFormalLineItemsHeld) {
    parts.push(`Full formal-statement line-item emission is still held: ${line.__ledgerAggregateFormalLineItemHeldReason}.`);
  }
  return parts.filter(Boolean).join(' ');
}

function LineProvenanceMarker({ line }) {
  const aggregateRow = line?.__ledgerAggregateRow || null;
  const aggregateProof = Boolean(
    aggregateRow?.proofPosture?.rowAuthorship === 'ledger-service-derived-journal-line-aggregate' ||
    aggregateRow?.proofPosture?.rowAuthorship === 'ledger-service-calculated-journal-line-aggregate' ||
    String(aggregateRow?.sourceLineage?.sourceClass || '').includes('ledger-service')
  );
  const moneyProvenance = line?.moneyProvenance || line?.fx || line?.__moneyProvenance || line?.__fx || null;
  const moneyClass = sourceClassForMoneyProvenance(moneyProvenance);
  const authoredByLedger = line?.authored === true || line?.proofPosture?.authoredByLedger === true;
  const explicitNonAuthored = line?.authored === false || line?.proofPosture?.rowAuthorship === 'not-authored';
  const sourceClass = (aggregateProof ? SOURCE_STATE_CLASSES.CONTRACT_BACKED : null)
    || moneyClass
    || (authoredByLedger ? SOURCE_STATE_CLASSES.CONTRACT_BACKED : null)
    || (explicitNonAuthored ? SOURCE_STATE_CLASSES.NON_AUTHORED : null);
  if (!sourceClass) return null;
  const tooltip = aggregateProof
    ? formatAggregateRowTooltip(line, aggregateRow)
    : moneyProvenance
    ? formatMoneyProvenanceTooltip(moneyProvenance)
    : authoredByLedger
      ? 'Ledger contract marks this row as authored by posted journal-line truth.'
      : 'Ledger contract marks this row as non-authored/non-strict.';
  return (
    <span className="relative z-[3] inline-flex ml-[4px] align-middle">
      <ProvenanceChip
        kind={{
          sourceClass,
          label: aggregateProof ? 'Ledger aggregate row' : moneyProvenance ? 'Money provenance' : 'Line authorship',
          compactLabel: aggregateProof
            ? 'aggregate-row'
            : moneyProvenance?.providerMode || moneyProvenance?.rateSource || (authoredByLedger ? 'authored' : 'held'),
        }}
        tooltip={tooltip}
        compact
      />
    </span>
  );
}

/* FormalStatementTable — filing-style rendering of an income statement,
   balance sheet, or cash flow statement.

   v8 — Tailwind rescue: the multi-period sticky-thead + sticky-label-column
   mechanics previously lived in multi-period.css. They now live inline as
   Tailwind utilities so there is no monolithic CSS file to truncate. */

/* v8 Path U — fmtAmount now lives in ./formatAmount.js (DRY across
   FormalStatementTable + MetricHorizonTable). Kept a thin wrapper here
   for backward-compat at the old 2-decimal default. */
function fmtAmount(v, parens = true, currencyCode = null) {
  if (!currencyCode) return sharedFmtAmount(v, parens, 2);
  if (v == null || v === '') return '';
  if (typeof v !== 'number') return v;
  if (!Number.isFinite(v)) return '';
  if (v === 0) return '—';
  if (v < 0 && parens) return `(${fmtMoney(Math.abs(v), { decimals: 2, currencyCode })})`;
  return fmtMoney(v, { decimals: 2, currencyCode });
}

/* v8 Path T — RowSparkSegment + SparkDefs now live in
   ./RowSparkSegment.jsx (DRY across FormalStatementTable + MetricHorizonTable). */

/* v8 Path AG — cell-class helpers + STICKY_LEFT_SHADOW moved to
   ./cellStyles.js (DRY across FormalStatementTable + MetricHorizonTable).
   Multi-mode sticky mechanics: LABEL column is sticky left-0 with a
   solid bg. THEAD row is sticky top-0. Corner cell is sticky on
   both axes at higher z. Header bg #1c1c30 originates from the
   multi-period.css legacy. */

function FormalStatementTableImpl({
  title, entity, period,
  periods, sections,
  sparklineGradient = false,
  currentPeriodLabel = null,
  currentPeriodProof = null,
  /* v8 Path U — DRY with MetricHorizonTable. Two toggles:
     - `showTrendColumn`: render the "Trend" column (MiniSparkline +
       delta glyph) between the label and the period columns
     - `showRowSparkline`: render in-cell underlay segments behind each
       amount (existing behaviour — preserved default true for back-compat) */
  showTrendColumn = false,
  showRowSparkline = true,
  /* v8 Path AI — when true, sparkline colors use metric polarity
     (line.betterWhen) instead of raw direction. Same contract as
     MetricHorizonTable. Off by default so existing tables don't
     change appearance. Income statement lines don't need annotation
     (expenses are stored as negatives, so rising=green naturally
     means "less spending = favorable"). Balance Sheet Liabilities
     are stored as positives and DO need `betterWhen:'lower'` on
     their shape lines — rising short-term debt would otherwise show green. */
  semanticTone = false,
  /* v8 Path AJ — `hideEmptyLines` filters lines where every amount
     across the horizon is zero or nullish. Filing convention renders
     zero as em-dash; multi-period historical views can end up with
     whole rows of em-dashes for events that only happen in forecast
     (debt paydown, custody outflow, loan inflow/repayment are all
     zero in backcast rows by design). Opt-in: FCC's 3-Statement Model
     uses this to de-noise historical columns. When all lines in a
     section get filtered, the section heading+total also hide. */
  hideEmptyLines = false,
  sourceState = null,
  ledgerAggregateRowProof = false,
  /* Guide controls — each defaults to true so the table still works when
     the caller doesn't pass `guides`. When false, the corresponding guide
     layer is suppressed on this table instance. */
  guides = {},
}) {
  const guidesOn = { tooltips: true, sparklineTrends: true, ...guides };
  const aggregateRowDecoration = React.useMemo(
    () => decorateStatementSectionsWithAggregateRowProof(sections || [], {
      sourceState,
      title,
      enableAggregateRowProof: ledgerAggregateRowProof,
    }),
    [sections, sourceState, title, ledgerAggregateRowProof]
  );
  const displaySections = aggregateRowDecoration.sections || sections || [];
  const isMulti = Array.isArray(periods) && periods.length > 1;
  const trendColVisible = isMulti && showTrendColumn && guidesOn.sparklineTrends;
  const scrollRef = React.useRef(null);
  useStickyCascade(scrollRef, 24);
  const tableMinW = isMulti ? 200 + (trendColVisible ? FORMAL_TREND_COL_WIDTH : 0) + periods.length * 130 : undefined;
  const multiTableStyle = isMulti
    ? {
        minWidth: tableMinW,
        '--lens-label-col-width': `${FORMAL_LABEL_COL_WIDTH}px`,
        '--lens-trend-col-width': `${FORMAL_TREND_COL_WIDTH}px`,
      }
    : undefined;
  const baseId = React.useId();
  const gradUpId = `spark-up-${baseId}`;
  const gradDownId = `spark-down-${baseId}`;
  const gradNeutralId = `spark-neutral-${baseId}`;
  /* v8 polish — current-period column highlight. Tab computes the label via
     fmtPeriod(currentYM(), density) and passes it down. If it matches one
     of the column headers we stripe the whole column gold so users always
     know where "now" sits regardless of direction or density. */
  const currentIdx = currentPeriodLabel
    ? periods.findIndex(p => p === currentPeriodLabel)
    : -1;
  const isCurrent = (i) => i === currentIdx;
  const renderedSections = displaySections
    .map((sec) => {
      if (!hideEmptyLines || !isMulti) return sec;
      const allZero = (amts) => {
        if (!Array.isArray(amts)) return amts == null || amts === 0;
        return amts.every(v => v == null || v === 0);
      };
      const keep = (ln) => !allZero(ln.amounts ?? ln.amount);
      const prunedLines = (sec.lines || []).filter(keep);
      const totalEmpty = !sec.total || allZero(sec.total.amounts ?? sec.total.amount);
      if (prunedLines.length === 0 && totalEmpty) return null;
      return { ...sec, lines: prunedLines };
    })
    .filter(Boolean);
  const trendStickyBase = `${STICKY_TREND_BODY_CELL} ${STICKY_TREND_BOX_GRID}`;
  const trendStickySection = `${STICKY_TREND_SECTION_CELL} ${STICKY_TREND_BOX_GRID}`;
  const labelStickyBox = STICKY_BOX_GRID;

  /* v8 Path AG — multi-period path uses shared <StatementFrame/>
     (same frame MetricHorizonTable uses). Single-period path keeps
     its filing-style compact layout (centered, narrower, inline
     header) — that's a distinct visual mode, not worth sharing. */
  const TableInner = (
    <table
      className={isMulti ? 'border-collapse text-[13px] table-fixed w-full' : 'w-full border-collapse text-[13px]'}
      style={multiTableStyle}
    >
          {isMulti ? (
            <>
              <colgroup>
                <col style={{ width: 'var(--lens-label-col-width)' }} />
                {trendColVisible && <col style={{ width: 'var(--lens-trend-col-width)' }} />}
                {periods.map((_, i) => <col key={i} style={{ minWidth: '110px' }} />)}
              </colgroup>
              {/* v8 Path X — thead's bottom line is an INSET shadow
                  (THEAD_BOTTOM_LINE / THEAD_CORNER_SHADOW). Stays pinned
                  with the sticky thead; no border-collapse collision
                  with tbody's first row. DRY with MetricHorizonTable. */}
              <thead>
                <tr>
                  <th className={`sticky left-0 top-0 z-[30] bg-[#1c1c30] text-left text-[0.72rem] font-semibold text-text-dim px-[0.4rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono ${THEAD_CORNER_SHADOW}`}>
                    Account
                  </th>
                  {trendColVisible && (
                    <th
                      className={`${STICKY_TREND_HEADER_CELL} text-center text-[0.72rem] font-semibold text-text-dim px-[0.4rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono ${STICKY_TREND_BOX_GRID}`}
                    >
                      Trend
                    </th>
                  )}
                  {periods.map((p, i) => (
                    <th
                      key={i}
                      className={`sticky top-0 z-[20] text-right text-[0.72rem] font-semibold px-[0.4rem] ${THEAD_VERT_PAD} whitespace-nowrap font-mono min-w-[110px] ${THEAD_BOTTOM_LINE} ${isCurrent(i) ? CURRENT_PERIOD_HEADER_CLS : DEFAULT_PERIOD_HEADER_CLS}`}
                      title={isCurrent(i) ? 'Current period' : undefined}
                    >
                      {isCurrent(i) ? `● ${p}` : p}
                    </th>
                  ))}
                </tr>
              </thead>
            </>
          ) : (
            <colgroup>
              <col style={{ width: '70%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
          )}

          {/* v8 Path Z — one <tbody> per section. CSS sticky scopes each
              sticky descendant to its nearest containing block; with one
              tbody per section, the section's H1 + H2 sticky rows are
              bounded to THAT section. When the user scrolls past the
              section, its tbody clips away and both H1 and H2 naturally
              un-stick — so the NEXT section's H1 takes over at the top
              (genuine "push upward" cascade, not stacked overlay).
              v8 Path AJ — `hideEmptyLines` filter applied here. Each
              section's `lines` array is pruned of rows whose amounts
              are all zero/nullish across the horizon. If the section
              is left with no lines AND its total is empty too, skip
              the section entirely (no heading, no total). Preserves
              filing-style em-dashes for partial-zero rows. */}
          {renderedSections.length > 0 ? renderedSections.map((sec, si) => (
            <tbody
              key={si}
              className={isMulti && si === 0 ? '[&>tr:first-child>td]:border-t-0 [&>tr:first-child>*]:border-t-0' : ''}
            >
              <React.Fragment>
                {sec.heading && (
                  <tr data-sticky-row="h1">
                    {isMulti ? (
                      <>
                        {/* v8 Path AC — multi-cell H1 row: label cell is
                            sticky-left to keep the heading visible while
                            the rest scrolls horizontally. Other cells are
                            solid-bg spacers so the label's overflow (if
                            the heading is longer than 180px) is visually
                            covered by the next cell's bg when scrolling
                            horizontally back to x=0. The colSpan banner
                            approach didn't sticky-left reliably. */}
                        <td className={`${STICKY_H_CELL_LEFT} bg-[#1c1c30] whitespace-nowrap px-2 py-0 font-bold text-[13px] text-accent-gold uppercase tracking-[0.05em] overflow-hidden text-ellipsis ${labelStickyBox}`} title={sec.heading}>
                          {sec.heading}
                        </td>
                        {trendColVisible && <td className={`${trendStickySection} py-0`} />}
                        {periods.map((_, i) => (
                          <td
                            key={i}
                            className={`${STICKY_H_CELL} py-0 ${isCurrent(i) ? CURRENT_PERIOD_STICKY_BG : 'bg-[#1c1c30]'}`}
                          />
                        ))}
                      </>
                    ) : (
                      <td colSpan={2} className="pt-[14px] pb-1 font-bold text-[13px] text-accent-gold uppercase tracking-[0.05em]">
                        {sec.heading}
                      </td>
                    )}
                  </tr>
                )}

                {sec.lines.map((ln, li) => {
                  const rowHover = isMulti
                    ? 'hover:[&>td]:bg-white/[0.03] hover:[&>td.formal-stmt__label-cell]:bg-[#1e1e3a]'
                    : '';
                  const rowBold = ln.bold ? 'font-bold' : '';

                  if (isMulti) {
                    const amts = Array.isArray(ln.amounts) ? ln.amounts : Array(periods.length).fill(ln.amount ?? 0);
                    /* v8 Path AK — magnitude-view resolution. For
                       always-negative currency lines, `plotAmts` becomes
                       the absolute-value series so the sparkline visually
                       matches the displayed parenthesized numbers. The
                       helper also infers betterWhen='lower' and forces
                       semantic behavior on that line, so rising magnitude
                       (more expense) shows red regardless of toggle.
                       Mixed-sign lines (e.g. Net income) fall through
                       unchanged. Cell rendering still uses raw `amts`
                       — parens-convention display is preserved. */
                    const trend = resolveTrendVisual(amts, 'currency', ln.betterWhen, semanticTone);
                    const { series: plotAmts, rising, tone: lnTone, flat } = trend;
                    const nums = plotAmts.filter(n => typeof n === 'number' && Number.isFinite(n));
                    const rowMin = nums.length ? Math.min(...nums) : 0;
                    const rowMax = nums.length ? Math.max(...nums) : 0;
                    return (
                      <tr key={li} className={`${rowBold} hover:[&>td]:bg-white/[0.03]`}>
                        <td className={`formal-stmt__label-cell hover:!bg-[#1e1e3a] ${labelCellCls({ isMulti, indent: ln.indent, rule: ln.rule })} ${labelStickyBox}`}>
                          <LabelWithTooltip label={ln.label} betterWhenFallback={ln.betterWhen} enabled={guidesOn.tooltips} />
                        </td>
                        {trendColVisible && (
                          <td
                            className={`${trendStickyBase} hover:!bg-[#1e1e3a] px-[0.3rem] py-1 text-center align-middle whitespace-nowrap`}
                          >
                            {flat ? (
                              <span className="text-text-dim text-[0.7rem]" title="No movement across horizon">—</span>
                            ) : (
                              <span className="inline-flex items-center gap-[4px]">
                                <MiniSparkline
                                  series={plotAmts}
                                  tone={lnTone}
                                  size="sm"
                                  gradient={sparklineGradient}
                                  highlightIdx={currentIdx >= 0 ? currentIdx : undefined}
                                  ariaLabel={`${ln.label} trend`}
                                />
                                <SparklineDeltaBadge series={plotAmts} format="currency" />
                              </span>
                            )}
                          </td>
                        )}
                        {amts.map((v, pi) => (
                          <td
                            key={pi}
                            className={`${amtCellCls({ isMulti, rule: ln.rule, isDouble: false })} ${isCurrent(pi) ? CURRENT_PERIOD_CELL_TINT : ''}`}
                          >
                            <span className="relative z-[2] [text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_0_1px_rgba(0,0,0,0.8)]">{fmtAmount(v, ln.parens !== false, ln.currency)}</span>
                            <LineProvenanceMarker line={ln} />
                            {showRowSparkline && !flat && pi > 0 && (
                              <RowSparkSegment
                                prev={plotAmts[pi - 1]}
                                curr={plotAmts[pi]}
                                min={rowMin}
                                max={rowMax}
                                rising={rising}
                                tone={lnTone}
                                gradient={sparklineGradient}
                                gradUpId={gradUpId}
                                gradDownId={gradDownId}
                                gradNeutralId={gradNeutralId}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  }

                  return (
                    <tr key={li} className={rowBold}>
                      <td className={labelCellCls({ isMulti, indent: ln.indent, rule: ln.rule })}>
                        <LabelWithTooltip label={ln.label} betterWhenFallback={ln.betterWhen} enabled={guidesOn.tooltips} />
                      </td>
                      <td className={amtCellCls({ isMulti, rule: ln.rule, isDouble: false })}>
                        {fmtAmount(ln.amount, ln.parens !== false, ln.currency)}
                        <LineProvenanceMarker line={ln} />
                      </td>
                    </tr>
                  );
                })}

                {sec.total && (() => {
                  const withRule = sec.total.rule !== false;
                  if (isMulti) {
                    const amts = Array.isArray(sec.total.amounts) ? sec.total.amounts : Array(periods.length).fill(sec.total.amount ?? 0);
                    /* v8 Path AK — magnitude-view for always-negative
                       section totals (e.g. Total operating expenses,
                       Total non-operating — both stored as negative
                       sums). Sparkline plots abs, betterWhen='lower'
                       inferred so rising total expense renders red
                       with line trending up. */
                    const totalTrend = resolveTrendVisual(amts, 'currency', sec.total.betterWhen, semanticTone);
                    const { series: totalPlotAmts, rising: totalRising, tone: totalTone, flat: totalFlat } = totalTrend;
                    const totalNums = totalPlotAmts.filter(n => typeof n === 'number' && Number.isFinite(n));
                    const totalRowMin = totalNums.length ? Math.min(...totalNums) : 0;
                    const totalRowMax = totalNums.length ? Math.max(...totalNums) : 0;
                    return (
                      /* v8 Path Y — H2 sticky (section total). Shares
                          top:32px with H1 so the two cascade: a newer
                          H1 pushes this H2 out of view when the next
                          section begins. Cells need solid bg so body
                          content doesn't bleed through the sticky row. */
                      <tr data-sticky-row="h2" className="font-bold hover:[&>td]:bg-white/[0.03] [&>td]:!pb-0">
                        <td className={`formal-stmt__label-cell ${STICKY_H_CELL_LEFT} bg-[#1c1c30] hover:!bg-[#1e1e3a] ${labelCellCls({ isMulti, rule: withRule })} ${labelStickyBox}`}>
                          <LabelWithTooltip label={sec.total.label} betterWhenFallback={sec.total.betterWhen} enabled={guidesOn.tooltips} />
                        </td>
                        {trendColVisible && (
                          <td
                            className={`${trendStickySection} hover:!bg-[#1e1e3a] px-[0.3rem] py-1 text-center align-middle whitespace-nowrap`}
                          >
                            {totalFlat ? (
                              <span className="text-text-dim text-[0.7rem]">—</span>
                            ) : (
                              <span className="inline-flex items-center gap-[4px]">
                                <MiniSparkline
                                  series={totalPlotAmts}
                                  tone={totalTone}
                                  size="sm"
                                  gradient={sparklineGradient}
                                  highlightIdx={currentIdx >= 0 ? currentIdx : undefined}
                                  ariaLabel={`${sec.total.label} trend`}
                                />
                                <SparklineDeltaBadge series={totalPlotAmts} format="currency" />
                              </span>
                            )}
                          </td>
                        )}
                        {amts.map((v, pi) => (
                          <td
                            key={pi}
                            className={`${STICKY_H_CELL} ${amtCellCls({ isMulti, rule: withRule, isDouble: true })} ${isCurrent(pi) ? CURRENT_PERIOD_STICKY_BG : 'bg-[#1c1c30]'}`}
                          >
                            <span className="relative z-[2] [text-shadow:0_1px_3px_rgba(0,0,0,0.85),0_0_1px_rgba(0,0,0,0.8)]">{fmtAmount(v, sec.total.parens !== false, sec.total.currency)}</span>
                            <LineProvenanceMarker line={sec.total} />
                            {showRowSparkline && !totalFlat && pi > 0 && (
                              <RowSparkSegment
                                prev={totalPlotAmts[pi - 1]}
                                curr={totalPlotAmts[pi]}
                                min={totalRowMin}
                                max={totalRowMax}
                                rising={totalRising}
                                tone={totalTone}
                                gradient={sparklineGradient}
                                gradUpId={gradUpId}
                                gradDownId={gradDownId}
                                gradNeutralId={gradNeutralId}
                              />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  }
                  return (
                    <tr className="font-bold">
                      <td className={labelCellCls({ isMulti, rule: withRule })}>{sec.total.label}</td>
                      <td className={amtCellCls({ isMulti, rule: withRule, isDouble: true })}>
                        {fmtAmount(sec.total.amount, sec.total.parens !== false, sec.total.currency)}
                        <LineProvenanceMarker line={sec.total} />
                      </td>
                    </tr>
                  );
                })()}
              </React.Fragment>
            </tbody>
          )) : (
            <tbody>
              <tr className="hover:[&>td]:bg-white/[0.03]">
                {isMulti ? (
                  <>
                    <td className={`formal-stmt__label-cell hover:!bg-[#1e1e3a] ${labelCellCls({ isMulti })} ${labelStickyBox}`}>
                      No statement lines
                    </td>
                    {trendColVisible && (
                      <td
                        className={`${trendStickyBase} hover:!bg-[#1e1e3a] px-[0.3rem] py-1 text-center align-middle whitespace-nowrap`}
                      >
                        <span className="text-text-dim text-[0.7rem]" title="No movement across horizon">—</span>
                      </td>
                    )}
                    {periods.map((_, i) => (
                      <td
                        key={i}
                        className={`${amtCellCls({ isMulti })} ${isCurrent(i) ? CURRENT_PERIOD_CELL_TINT : ''}`}
                      >
                        <span className="relative z-[2] text-text-dim">—</span>
                      </td>
                    ))}
                  </>
                ) : (
                  <td colSpan={2} className="py-[3px] text-text-dim">
                    No statement lines
                  </td>
                )}
              </tr>
            </tbody>
          )}
        </table>
  );

  if (isMulti) {
    return (
      <StatementFrame
        ref={scrollRef}
        entity={entity}
        title={title}
        period={period}
        sourceState={sourceState}
        defs={sparklineGradient && <SparkDefs upId={gradUpId} downId={gradDownId} neutralId={gradNeutralId} />}
        pinnedChrome={<CurrentPeriodProofBadge label={currentPeriodLabel} found={currentIdx >= 0} proof={currentPeriodProof} />}
      >
        {TableInner}
      </StatementFrame>
    );
  }

  /* Single-period path — compact filing layout, no scroll mechanics,
     narrower centered card. StatementFrame is reserved for the
     wide, dense, scrollable multi-period layout. */
  return (
    <div className="font-sans bg-white/[0.015] border-2 border-border-strong rounded-lg shadow-sm px-7 py-5 max-w-[760px] mx-auto">
      <div className="-mx-7 -mt-5 mb-[18px]">
        <StatementHeader entity={entity} title={title} period={period} sourceState={sourceState} collapsible={false} />
      </div>
      {TableInner}
    </div>
  );
}

/* Memoized — formal statement tables are the single heaviest render in
   the app (dozens of rows × up to 12 period columns × per-cell SVGs).
   Memoizing skips re-renders when sections/periods haven't changed. */
const FormalStatementTable = React.memo(FormalStatementTableImpl);
export default FormalStatementTable;

/* Pre-shaped builders (unchanged from v7.4) */
export function shapeIncomeStatement(is) {
  return [
    {
      heading: 'Revenue',
      lines: [
        { label: 'Primary income (gross)',        amount: is.revenue, indent: 1 },
        { label: 'Recurring revenue',             amount: is.fvf,     indent: 1 },
      ],
      total: { label: 'Total revenue', amount: is.revenue + is.fvf },
    },
    {
      heading: 'Operating expenses',
      lines: [
        { label: 'Subscriptions (recurring)',  amount: -is.subsExpense, indent: 1 },
        { label: 'Direct operating AP',        amount: -is.familyDirect, indent: 1 },
        { label: 'Partner-channel AP',         amount: -is.marianExpense, indent: 1 },
      ],
      total: { label: 'Total operating expenses', amount: -(is.subsExpense + is.familyDirect + is.marianExpense) },
    },
    {
      heading: 'Non-operating',
      lines: [
        { label: 'Term debt interest',    amount: -is.mortgagesInterestPortion, indent: 1 },
        { label: 'Withholding tax',       amount: -is.wht, indent: 1 },
        { label: 'Statutory contributions + fees', amount: -is.gov, indent: 1 },
      ],
      total: { label: 'Total non-operating', amount: -(is.mortgagesInterestPortion + is.wht + is.gov) },
    },
    {
      lines: [],
      total: { label: 'Net income', amount: is.netIncome, rule: true },
    },
  ];
}

export function shapeBalanceSheet({ assetsLiquid, fixedAssetsNet, ccDebt, ccCredits, ltl, eq, susTotal }) {
  const fixed = fixedAssetsNet ?? 0;
  const totalAssets = assetsLiquid + fixed;
  return [
    {
      heading: 'Assets',
      lines: [
        { label: 'Liquid assets — bank + cash + e-wallets', amount: assetsLiquid, indent: 1 },
        ...(fixed > 0 ? [{ label: 'NC-Assets (net book value)', amount: fixed, indent: 1 }] : []),
      ],
      total: { label: 'Total assets', amount: totalAssets },
    },
    {
      heading: 'Liabilities',
      /* v8 Path AI — betterWhen:'lower' on each line + total. Even
         though single-period doesn't draw sparklines today, keeping
         the annotation consistent in case single-period gains trend
         visuals later. Short-term credits line is stored as negative (it's a
         credit-back reducing debt) so its direction already makes
         sense under default rising=good. */
      lines: [
        { label: 'Short-term debt (outstanding)', amount: ccDebt, indent: 1, betterWhen: 'lower' },
        /* Stored as negative to offset short-term debt in the Liabilities block.
           Magnitude-flip would default betterWhen='lower', but MORE
           credits is better for the book, so declare 'higher' explicitly. */
        { label: 'Short-term credits',             amount: -ccCredits, indent: 1, betterWhen: 'higher' },
        { label: 'Long-term liabilities',          amount: ltl, indent: 1, betterWhen: 'lower' },
      ],
      total: { label: 'Total liabilities', amount: ccDebt - ccCredits + ltl, betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: [
        { label: 'Owner equity (proxy)', amount: eq, indent: 1 },
        { label: 'Suspense balance',     amount: susTotal, indent: 1 },
      ],
      total: { label: 'Total equity', amount: eq + susTotal },
    },
    {
      lines: [],
      total: {
        label: 'Total liabilities + equity',
        amount: (ccDebt - ccCredits + ltl) + (eq + susTotal),
        rule: true,
        betterWhen: 'neutral',   /* accounting identity — see multi version */
      },
    },
  ];
}

export function shapeCashFlowStatement(cfRow) {
  return [
    {
      heading: 'Operating activities',
      lines: [
        { label: 'Net income',          amount: cfRow.netIncome, indent: 1 },
        { label: 'Δ Accounts payable',  amount: cfRow.deltaAP, indent: 1 },
      ],
      total: { label: 'Net cash from operations (CFO)', amount: cfRow.cfo },
    },
    {
      heading: 'Investing activities',
      lines: [
        { label: 'Custody reserve outflow', amount: cfRow.custodyOut, indent: 1 },
      ],
      total: { label: 'Net cash from investing (CFI)', amount: cfRow.cfi },
    },
    {
      heading: 'Financing activities',
      lines: [
        { label: 'Debt paydown',         amount: cfRow.ccPaydown, indent: 1 },
        { label: 'Mortgage principal',   amount: cfRow.mortgagePrincipal, indent: 1 },
        { label: 'Loan inflow',          amount: cfRow.jmLumpIn, indent: 1 },
        { label: 'Loan repayment',       amount: cfRow.jmRepayOut, indent: 1 },
      ],
      total: { label: 'Net cash from financing (CFF)', amount: cfRow.cff },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amount: cfRow.netCashChange, rule: true },
    },
  ];
}

export function shapeIncomeStatementMulti(isArray) {
  if (!Array.isArray(isArray) || isArray.length === 0) return [];
  const pull = (pickers) => isArray.map(is => pickers.reduce((v, k) => v ?? is?.[k], undefined) ?? 0);
  return [
    {
      heading: 'Revenue',
      lines: [
        { label: 'Primary income (gross)',        amounts: pull(['salaryGross', 'revenue']), indent: 1 },
        { label: 'Recurring revenue',             amounts: pull(['fvfRevenue', 'fvf']),       indent: 1 },
        { label: 'Support contrib / AR',          amounts: pull(['familyContribRevenue']),    indent: 1 },
      ],
      total: {
        label: 'Total revenue',
        amounts: isArray.map(is => is?.totalRevenue ?? ((is?.salaryGross ?? is?.revenue ?? 0) + (is?.fvfRevenue ?? is?.fvf ?? 0) + (is?.familyContribRevenue ?? 0))),
      },
    },
    {
      heading: 'Operating expenses',
      lines: [
        { label: 'Subscriptions (recurring)',  amounts: isArray.map(is => -(is?.subscriptionsExpense ?? is?.subsExpense ?? 0)), indent: 1 },
        { label: 'Direct operating AP',        amounts: isArray.map(is => -(is?.familyExpense ?? is?.familyDirect ?? 0)), indent: 1 },
        { label: 'Partner-channel AP',         amounts: isArray.map(is => -(is?.marianExpense ?? 0)), indent: 1 },
        { label: 'Fuel',                       amounts: isArray.map(is => -(is?.fuelExpense ?? 0)), indent: 1 },
        { label: 'Seasonal events',            amounts: isArray.map(is => -(is?.seasonalExpense ?? 0)), indent: 1 },
        { label: 'Lifestyle',                  amounts: isArray.map(is => -(is?.lifestyleExpense ?? 0)), indent: 1 },
      ],
      total: {
        label: 'Total operating expenses',
        amounts: isArray.map(is => -(is?.totalOpEx ?? ((is?.subscriptionsExpense ?? is?.subsExpense ?? 0) + (is?.familyExpense ?? is?.familyDirect ?? 0) + (is?.marianExpense ?? 0) + (is?.fuelExpense ?? 0) + (is?.seasonalExpense ?? 0) + (is?.lifestyleExpense ?? 0)))),
      },
    },
    {
      heading: 'Non-operating',
      lines: [
        { label: 'Term debt interest',         amounts: isArray.map(is => -(is?.mortgageInterest ?? is?.mortgagesInterestPortion ?? 0)), indent: 1 },
        { label: 'Withholding tax',            amounts: isArray.map(is => -(is?.wht ?? 0)), indent: 1 },
        { label: 'Statutory contributions + fees', amounts: isArray.map(is => -(is?.gov ?? 0)), indent: 1 },
      ],
      total: {
        label: 'Total non-operating',
        amounts: isArray.map(is => -((is?.mortgageInterest ?? is?.mortgagesInterestPortion ?? 0) + (is?.wht ?? 0) + (is?.gov ?? 0))),
      },
    },
    {
      lines: [],
      total: { label: 'Net income', amounts: isArray.map(is => is?.netIncome ?? 0), rule: true },
    },
  ];
}

export function shapeBalanceSheetMulti(bsArray) {
  if (!Array.isArray(bsArray) || bsArray.length === 0) return [];
  return [
    {
      heading: 'Assets',
      lines: [
        { label: 'Cash + e-wallets',           amounts: bsArray.map(bs => bs?.cash ?? bs?.assetsLiquid ?? 0), indent: 1 },
        { label: 'Custody receivable',         amounts: bsArray.map(bs => bs?.custodyReceivable ?? 0), indent: 1 },
        { label: 'Accounts receivable',        amounts: bsArray.map(bs => bs?.accountsReceivable ?? 0), indent: 1 },
        { label: 'NC-Assets', amounts: bsArray.map(bs => bs?.realEstateCost ?? bs?.fixedAssetsNet ?? 0), indent: 1 },
      ],
      total: { label: 'Total assets', amounts: bsArray.map(bs => bs?.totalAssets ?? 0) },
    },
    {
      heading: 'Liabilities',
      /* v8 Path AI — Liabilities are stored as POSITIVE numbers (unlike
         IS expenses which are negative). So `rising` of 100→200 means
         more debt = unfavorable. betterWhen='lower' makes the sparkline
         color reflect this when Semantic tone is on. */
      lines: [
        { label: 'Short-term debt payable', amounts: bsArray.map(bs => bs?.creditCardsPayable ?? bs?.ccDebt ?? 0), indent: 1, betterWhen: 'lower' },
        { label: 'Accounts payable',     amounts: bsArray.map(bs => bs?.accountsPayable ?? 0), indent: 1, betterWhen: 'lower' },
        { label: 'Term debt payable',    amounts: bsArray.map(bs => bs?.mortgagesPayable ?? bs?.ltl ?? 0), indent: 1, betterWhen: 'lower' },
      ],
      total: { label: 'Total liabilities', amounts: bsArray.map(bs => bs?.totalLiabilities ?? 0), betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: [
        { label: 'Total equity (A − L)', amounts: bsArray.map(bs => bs?.totalEquity ?? 0), indent: 1 },
      ],
      total: {
        label: 'Total L + E',
        amounts: bsArray.map(bs => (bs?.totalLiabilities ?? 0) + (bs?.totalEquity ?? 0)),
        rule: true,
        /* v8 Path AI — accounting identity (always equals Total Assets
           by construction). Rising/falling here reflects "balance sheet
           is growing/shrinking" which is neither inherently good nor
           bad — it's mechanical. Neutral polarity renders a gray line
           under Semantic tone instead of misleading green/red. */
        betterWhen: 'neutral',
      },
    },
  ];
}

export function shapeCashFlowStatementMulti(cfArray) {
  if (!Array.isArray(cfArray) || cfArray.length === 0) return [];
  return [
    {
      heading: 'Operating activities',
      lines: [
        { label: 'Net income',         amounts: cfArray.map(cf => cf?.netIncome ?? 0), indent: 1 },
        { label: 'Δ Accounts payable', amounts: cfArray.map(cf => cf?.deltaAP ?? 0), indent: 1 },
      ],
      total: { label: 'Net cash from operations (CFO)', amounts: cfArray.map(cf => cf?.cfo ?? 0) },
    },
    {
      heading: 'Investing activities',
      lines: [
        { label: 'Custody outflow',    amounts: cfArray.map(cf => cf?.custodyOut ?? 0), indent: 1 },
      ],
      total: { label: 'Net cash from investing (CFI)', amounts: cfArray.map(cf => cf?.cfi ?? 0) },
    },
    {
      heading: 'Financing activities',
      lines: [
        { label: 'Debt paydown',       amounts: cfArray.map(cf => cf?.ccPaydown ?? 0), indent: 1 },
        { label: 'Mortgage principal', amounts: cfArray.map(cf => cf?.mortgagePrincipal ?? 0), indent: 1 },
        { label: 'Loan inflow',        amounts: cfArray.map(cf => cf?.jmLumpIn ?? 0), indent: 1 },
        { label: 'Loan repayment',     amounts: cfArray.map(cf => cf?.jmRepayOut ?? 0), indent: 1 },
      ],
      total: { label: 'Net cash from financing (CFF)', amounts: cfArray.map(cf => cf?.cff ?? 0) },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amounts: cfArray.map(cf => cf?.netCashChange ?? 0), rule: true },
    },
  ];
}
